// A sheet from the bottom pulls down to close (UX-40, B-311, DIRECTION U20).
//
// Apple's sheets and Material's bottom sheets both close by being slid down,
// from their grabber or from their content once it is scrolled to its top
// ("a sheet expands when people scroll its contents or drag the grabber";
// Material's modal sheets are "dismissed by completely sliding them down").
// The web has no sheet of its own, so this is ours: a finger that starts on
// the sheet and moves down, where nothing under it would scroll up instead,
// carries the sheet with it; let go past a third of its height, or with a
// flick, and it closes; otherwise it springs back. The visible Close stays
// (UX-3): this is a second way, never the only one.
//
// Touch only: a mouse has the Close, and a desktop's sheets come from the
// side. Rendered inside the drawer, it finds the drawer's content and moves
// that, which is a style property set by hand — so it lives in the kit.

import { useEffect, useRef } from 'react';

/** How far down, as a share of the sheet's height, closes it on release. */
const CLOSE_AT = 1 / 3;
/** A flick this fast (px per ms) downward closes it whatever the distance. */
const FLICK = 0.6;
/** Movement before a touch counts as a drag rather than a tap. */
const SLOP = 8;

/** Is something between `from` and `sheet` scrolled down, so a pull scrolls it instead? */
function scrolledAbove(from: Element | null, sheet: HTMLElement): boolean {
  for (let el = from; el !== null && el !== sheet.parentElement; el = el.parentElement) {
    if (!(el instanceof HTMLElement)) continue;
    const style = getComputedStyle(el);
    const scrolls = /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
    if (scrolls && el.scrollTop > 0) return true;
    if (el === sheet) break;
  }
  return false;
}

/** A touch that starts here is the page's, never the sheet's: typing, a slider, a sideways list. */
function ownsTouch(target: Element | null): boolean {
  return (
    target?.closest(
      'input, textarea, select, [contenteditable="true"], [role="slider"], .fsp-table-scroll[data-sideways]',
    ) != null
  );
}

export function PullDown({ onClose }: { onClose: () => void }) {
  const marker = useRef<HTMLSpanElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const sheet = marker.current?.closest<HTMLElement>('.mantine-Drawer-content') ?? null;
    if (sheet === null) return;
    let startY = 0;
    let startX = 0;
    let dragging = false;
    let refused = false;
    let lastY = 0;
    let lastT = 0;
    let speed = 0;

    // MANTINE'S OWN, PUT BACK. The drawer's slide in and out is inline style
    // its transition sets once (the property, duration and curve) and React
    // only writes again when a value changes, so a drag that cleared them left
    // the next close with no slide at all (the review of 2026-10-13). What it
    // had is kept here when a drag starts and written back when it ends.
    let saved: Record<'transform' | 'property' | 'duration' | 'timing' | 'delay', string> | null =
      null;
    const keep = () => {
      if (saved !== null) return;
      const st = sheet.style;
      saved = {
        transform: st.transform,
        property: st.transitionProperty,
        duration: st.transitionDuration,
        timing: st.transitionTimingFunction,
        delay: st.transitionDelay,
      };
    };
    /** Mantine's values again; on a close the sheet stays where the pull left it. */
    const putBack = (keepPlace = false) => {
      if (saved === null) return;
      const st = sheet.style;
      if (!keepPlace) st.transform = saved.transform;
      st.transitionProperty = saved.property;
      st.transitionDuration = saved.duration;
      st.transitionTimingFunction = saved.timing;
      st.transitionDelay = saved.delay;
      saved = null;
    };
    const moveWith = (ms: number, curve: string, transform: string) => {
      const st = sheet.style;
      st.transitionProperty = ms > 0 ? 'transform' : 'none';
      st.transitionDuration = `${ms}ms`;
      st.transitionTimingFunction = curve;
      st.transitionDelay = '0ms';
      st.transform = transform;
    };
    const springBack = () => {
      moveWith(200, 'cubic-bezier(0.2, 0, 0, 1)', saved?.transform || 'none');
      window.setTimeout(putBack, 220);
    };

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (event.touches.length !== 1 || touch === undefined) {
        refused = true;
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      // The title bar is the grabber's: it pulls the sheet however far its
      // content is scrolled, as Apple's grabber does.
      const onTitle = target?.closest('.mantine-Drawer-header') != null;
      refused = ownsTouch(target) || (!onTitle && scrolledAbove(target, sheet));
      dragging = false;
      startY = lastY = touch.clientY;
      startX = touch.clientX;
      lastT = event.timeStamp;
      speed = 0;
    };

    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (refused || touch === undefined) return;
      const dy = touch.clientY - startY;
      const dx = touch.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) > SLOP && Math.abs(dx) > Math.abs(dy)) {
          refused = true; // sideways: a list's, not ours
          return;
        }
        if (dy < -SLOP) {
          refused = true; // up: the content scrolls
          return;
        }
        if (dy <= SLOP) return;
        dragging = true;
        keep();
      }
      event.preventDefault();
      const dt = Math.max(1, event.timeStamp - lastT);
      speed = (touch.clientY - lastY) / dt;
      lastY = touch.clientY;
      lastT = event.timeStamp;
      moveWith(0, 'linear', `translateY(${Math.max(0, dy)}px)`);
    };

    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      const moved = lastY - startY;
      if (moved > sheet.getBoundingClientRect().height * CLOSE_AT || speed > FLICK) {
        // Out the rest of the way, then closed: the drawer's own exit starts
        // where this one ends, so nothing jumps.
        moveWith(180, 'cubic-bezier(0.3, 0, 0.8, 0.15)', 'translateY(100%)');
        window.setTimeout(() => {
          putBack(true);
          close.current();
        }, 170);
      } else {
        springBack();
      }
    };

    sheet.addEventListener('touchstart', onStart, { passive: true });
    sheet.addEventListener('touchmove', onMove, { passive: false });
    sheet.addEventListener('touchend', onEnd);
    sheet.addEventListener('touchcancel', onEnd);
    return () => {
      sheet.removeEventListener('touchstart', onStart);
      sheet.removeEventListener('touchmove', onMove);
      sheet.removeEventListener('touchend', onEnd);
      sheet.removeEventListener('touchcancel', onEnd);
      putBack();
    };
  }, []);

  return <span ref={marker} hidden />;
}
